/**
 * The one-customer-one-company refusal, in a form a person can act on.
 *
 * `cqc_assert_single_owner()` enforces BOTH link directions and raises one of:
 *   QUICKBOOKS_CUSTOMER_TAKEN: QuickBooks customer <id> is already linked to company "<name>" (<uuid>)
 *   QUICKBOOKS_COMPANY_CUSTOMER_TAKEN: this company already bills to QuickBooks customer <id> ("<name>") in realm <realm>
 *
 * The first is "that customer belongs to someone else"; the second is "this
 * company already has a billing customer in that company file". Different fix,
 * so different sentence — but both are 409s, never 500s.
 *
 * Every transport must turn that into a sentence that names the incumbent and
 * the two real fixes — unlink, or merge the duplicate Hub companies. Shipped as
 * shared helpers (not inline string checks) because the refusal surfaces on at
 * least four paths: the QuickBooks entity API route, the customer-link chat
 * tool, the reconciliation link service, and any pg_graphql company edit that
 * writes the mirror column.
 *
 * Pure + isomorphic — no DB, no server-only imports — so client code can
 * recognize the refusal too. Mirrors `lib/utils/contact/method-keys.ts`, which
 * does the same job for phone/email/domain exclusivity.
 */

const CONFLICT_MARKER = "QUICKBOOKS_CUSTOMER_TAKEN";
const COMPANY_CONFLICT_MARKER = "QUICKBOOKS_COMPANY_CUSTOMER_TAKEN";

export interface QuickBooksCustomerConflict {
  customerId: string;
  /** Company that already holds the customer, when the DB resolved a name. */
  incumbentCompanyName: string | null;
  incumbentCompanyId: string | null;
}

interface PgErrorLike {
  code?: string;
  message?: string;
  details?: string;
  detail?: string;
  hint?: string;
}

function asPgError(error: unknown): PgErrorLike | null {
  if (typeof error !== "object" || error === null) return null;
  const candidate = error as PgErrorLike;
  if (
    typeof candidate.message !== "string" &&
    typeof candidate.details !== "string" &&
    typeof candidate.detail !== "string"
  ) {
    return null;
  }
  return candidate;
}

/** True when this error is the one-owner constraint refusing a link. */
export function isQuickBooksCustomerConflictError(error: unknown): boolean {
  const pg = asPgError(error);
  if (!pg) return false;
  return [pg.message, pg.details, pg.detail].some(
    (part) => typeof part === "string" && part.includes(CONFLICT_MARKER)
  );
}

/**
 * Recover the conflict details from the raised message.
 *
 * Returns null when the error is not a QuickBooks link conflict — including the
 * unique-index violation (23505), which carries no owner detail; callers that
 * hit that path should report a generic "already linked" and let the caller
 * re-read the link list.
 */
export function parseQuickBooksCustomerConflict(
  error: unknown
): QuickBooksCustomerConflict | null {
  const pg = asPgError(error);
  if (!pg) return null;
  const haystack = [pg.message, pg.details, pg.detail]
    .filter((part): part is string => typeof part === "string")
    .join(" | ");

  const match =
    /QUICKBOOKS_CUSTOMER_TAKEN:\s*QuickBooks customer\s+(\S+)\s+is already linked to company\s+"(.*?)"\s*\(([^)]*)\)/.exec(
      haystack
    );
  if (!match) return null;

  const name = match[2] ?? "";
  const id = match[3] ?? "";
  return {
    customerId: match[1] ?? "",
    incumbentCompanyName: name && name !== "?" ? name : null,
    incumbentCompanyId: id && id !== "?" ? id : null,
  };
}

/** The sentence a user sees. States the owner, the why, and both fixes. */
export function quickBooksCustomerConflictMessage(
  conflict: QuickBooksCustomerConflict
): string {
  const owner = conflict.incumbentCompanyName
    ? `"${conflict.incumbentCompanyName}"`
    : "another company";
  return (
    `That QuickBooks customer is already linked to ${owner}. ` +
    `A QuickBooks customer bills one company, so it can only be linked to one — ` +
    `otherwise invoices and billing reports split across two records. ` +
    `Two ways to fix it: unlink it from ${owner} first if this is the right company, ` +
    `or if both records are the same business, merge the companies.`
  );
}

/**
 * The reverse refusal: this company already bills from a customer in this realm.
 *
 * Raised when a link would give one company TWO primary customers in a single
 * QuickBooks company file, which is what leaves reconciliation without a single
 * answer to "who do we invoice?".
 */
export interface QuickBooksCompanyCustomerConflict {
  /** Customer this company already bills from, in this realm. */
  heldCustomerId: string;
  heldCustomerName: string | null;
  realmId: string | null;
}

/** True when the refusal is "this company already has a billing customer". */
export function isQuickBooksCompanyCustomerConflictError(error: unknown): boolean {
  const pg = asPgError(error);
  if (!pg) return false;
  return [pg.message, pg.details, pg.detail].some(
    (part) => typeof part === "string" && part.includes(COMPANY_CONFLICT_MARKER)
  );
}

/** Recover which customer the company already bills from. */
export function parseQuickBooksCompanyCustomerConflict(
  error: unknown
): QuickBooksCompanyCustomerConflict | null {
  const pg = asPgError(error);
  if (!pg) return null;
  const haystack = [pg.message, pg.details, pg.detail]
    .filter((part): part is string => typeof part === "string")
    .join(" | ");

  const match =
    /QUICKBOOKS_COMPANY_CUSTOMER_TAKEN:\s*this company already bills to QuickBooks customer\s+(\S+)\s+\("(.*?)"\)\s+in realm\s+(\S+)/.exec(
      haystack
    );
  if (!match) return null;

  const name = match[2] ?? "";
  const realm = match[3] ?? "";
  return {
    heldCustomerId: match[1] ?? "",
    heldCustomerName: name && name !== "?" ? name : null,
    realmId: realm && realm !== "?" ? realm : null,
  };
}

/** The sentence a user sees for the reverse refusal. Names both fixes. */
export function quickBooksCompanyCustomerConflictMessage(
  conflict: QuickBooksCompanyCustomerConflict | null
): string {
  const held = conflict?.heldCustomerName
    ? `"${conflict.heldCustomerName}"`
    : conflict?.heldCustomerId
      ? `customer ${conflict.heldCustomerId}`
      : "another QuickBooks customer";
  return (
    `This company already bills from ${held} in that QuickBooks company file. ` +
    `A company invoices from one customer per company file, so billing, the ` +
    `recurring-invoice cron and the billing report all have a single answer. ` +
    `Two ways forward: unlink ${held} if it is the wrong customer, or keep both ` +
    `by linking this one as an additional (secondary) customer.`
  );
}
